import { useState } from 'react'
import { getPosition, type Coords, type GeoFailure } from '../lib/geo.js'
import { t } from '../lib/i18n.js'
import { LANDMARKS, searchLandmarks } from '../lib/landmarks.js'
import { ChevronRight, LANDMARK_ICON, MapPin, Navigation, Search, Spinner } from './icons.js'

/**
 * Choosing where you are travelling FROM - spec §11.
 *
 * GPS is the default because "share location is one tap" and text fields are
 * where consumer funnels die. But GPS answers "where am I now", and the
 * question is "where will I be coming from", which is not always the same:
 * create a link from the office for a meeting you will travel to from home
 * and GPS is wrong by two hours. That is a venue in the wrong place, so a
 * custom origin clears invariant 8's bar.
 *
 * Shared by both screens. Alice needs it more (she creates links in advance),
 * but Bob hits the same problem whenever he opens the link somewhere he will
 * not be.
 */

export interface PickedOrigin extends Coords {
  label?: string
}

interface Props {
  gpsLabel?: string
  /** A previously used origin, offered as the zero-input path (§11 point 5). */
  remembered?: PickedOrigin | null
  busy?: boolean
  onPick: (origin: PickedOrigin) => void
}

export function OriginPicker({ gpsLabel, remembered, busy, onPick }: Props) {
  const [mode, setMode] = useState<'buttons' | 'search'>('buttons')
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function useGps() {
    setLocating(true)
    setError(null)
    try {
      onPick(await getPosition())
    } catch (err) {
      // A refusal is not a failure - it is a different path. Open the search
      // immediately rather than leaving a dead end (invariant 9).
      const fail = err as GeoFailure
      setError(t(fail.kind === 'denied' ? 'picker.denied' : 'picker.unavailable'))
      setMode('search')
    } finally {
      setLocating(false)
    }
  }

  if (mode === 'search') {
    return (
      <div>
        {error && (
          <p className="faint" style={{ color: 'var(--warn)' }} role="status">
            {error}
          </p>
        )}
        <LandmarkSearch onPick={onPick} />
        <button
          className="btn btn-ghost mt-sm"
          onClick={() => {
            setError(null)
            setMode('buttons')
          }}
        >
          <Navigation size={16} aria-hidden />
          {t('picker.useGps')}
        </button>
      </div>
    )
  }

  return (
    <div className="stack">
      {/* Only offer a remembered origin that has a NAME. A saved GPS fix is
          just coordinates, and a button reading "where you were last time"
          asks someone to trust a place they cannot see - worse than no
          shortcut at all. */}
      {remembered?.label && (
        <button className="btn" disabled={busy} onClick={() => onPick(remembered)}>
          <MapPin size={18} aria-hidden />
          {remembered.label}
        </button>
      )}

      <button className="btn" disabled={busy || locating} onClick={() => void useGps()}>
        {locating ? <Spinner /> : <Navigation size={18} aria-hidden />}
        {locating ? t('picker.locating') : (gpsLabel ?? t('picker.gps'))}
      </button>

      {/* Always available, never buried - the "I am at work" case is common. */}
      <button className="btn btn-ghost" disabled={busy} onClick={() => setMode('search')}>
        <Search size={16} aria-hidden />
        {t('picker.elsewhere')}
      </button>
    </div>
  )
}

/**
 * Transit stations first - "Westbahnhof" beats a street address to type on a
 * phone (§11 point 4).
 *
 * A hand-verified list in v1. Places Autocomplete is the upgrade, but it needs
 * its own session-token billing story and does not belong in the cold-
 * recipient path untested.
 */
function LandmarkSearch({ onPick }: { onPick: (o: PickedOrigin) => void }) {
  const [q, setQ] = useState('')
  const matches = q.trim() ? searchLandmarks(q) : LANDMARKS.slice(0, 6)

  return (
    <div>
      <input
        className="input"
        placeholder={t('picker.search')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
      />
      <div className="stack mt-sm">
        {matches.map((l) => {
          const Icon = LANDMARK_ICON[l.kind]
          return (
            <button
              key={l.name}
              className="row"
              onClick={() => onPick({ lat: l.lat, lng: l.lng, label: l.name })}
            >
              <Icon size={18} aria-hidden />
              <span className="row-main">
                <span className="row-title">{l.name}</span>
              </span>
              <ChevronRight size={16} aria-hidden />
            </button>
          )
        })}
        {matches.length === 0 && <p className="muted center">{t('picker.noMatch')}</p>}
      </div>
    </div>
  )
}
